import React, { useState } from "react";
import { audioSupported } from "../../lib/keyboardPlan";
import {
  drillKindsFor,
  drillStatOf,
  timerUnlocked,
  fmtMs,
  levelOf,
  keyLabel,
  MASTERY_MAX,
  MIX_KEY,
  SESSION_LENGTHS,
  DEFAULT_LENGTH,
} from "../../lib/numberSenseV2";
import { Icon, Keys } from "../numberSenseUi";

const CAN_PLAY = audioSupported();

/* ============================================================
   Number Sense v2 — the drill menu for one (scale type, key):
   pick a focused drill (degrees, next note, chord qualities, the
   recipe) or the hearts-on mixed set, and how long to run it.
   Each card shows your record for that drill in this key (runs +
   best accuracy), so redoing it is a ladder, not a loop.
   ============================================================ */

const KIND_ICONS = {
  mixed: "play",
  degrees: "grid",
  climb: "bolt",
  chords: "piano",
  "chord-build": "piano",
  "chord-ear": "play",
  pattern: "book",
  "inv-build": "piano",
  "inv-name": "grid",
  "inv-ear": "play",
};

const DrillMenuScreen = ({
  type,
  keyName,
  mastery,
  drillStats,
  onStart,
  onWalk,
  onChordWalk,
  onInvWalk,
  onClose,
}) => {
  const [length, setLength] = useState(DEFAULT_LENGTH);
  const where = keyName === MIX_KEY ? "mixed keys" : keyLabel(type, keyName);
  const level = keyName === MIX_KEY ? null : levelOf(mastery, type.id, keyName);
  const kinds = drillKindsFor(type).filter((k) => !k.needsAudio || CAN_PLAY);
  const scaleKinds = kinds.filter((k) => k.group === "scale");
  const chordKinds = kinds.filter((k) => k.group === "chords");
  const invKinds = kinds.filter((k) => k.group === "inversions");

  const kindCard = (kind) => {
    const stat = drillStatOf(drillStats, type.id, keyName, kind.id);
    const timed = timerUnlocked(drillStats, type.id, keyName, kind.id);
    return (
      <button
        className={`continue-card v2-kind-card ${
          kind.hearts ? "v2-kind-mixed" : ""
        }`}
        key={kind.id}
        onClick={() => onStart(kind.id, length)}
      >
        <div className="ic">
          <Icon name={KIND_ICONS[kind.id] || "play"} />
        </div>
        <div className="txt">
          <div className="kicker">
            {kind.hearts ? "the test · hearts on" : "practice"}
          </div>
          <div className="ttl">{kind.title}</div>
          <div className="sub">{kind.gloss}</div>
        </div>
        {timed && stat.bestMs ? (
          // accuracy is beaten — the ladder is now your clean time
          <div className="v2-kind-stat">
            <b className="mono">
              <Icon name="timer" /> {fmtMs(stat.bestMs)}
            </b>
            <small>
              best clean · {stat.runs} run{stat.runs === 1 ? "" : "s"}
            </small>
          </div>
        ) : stat ? (
          <div className="v2-kind-stat">
            <b className="mono">{stat.best}%</b>
            <small>
              best · {stat.runs} run{stat.runs === 1 ? "" : "s"}
            </small>
          </div>
        ) : (
          <span className="chev">›</span>
        )}
      </button>
    );
  };

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onClose} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">{where}</div>
        </div>
        <div className="scroll">
          {level != null && (
            <div className="v2-menu-level">
              {level >= MASTERY_MAX ? (
                <>
                  <Icon name="trophy" /> crowned
                </>
              ) : (
                <>
                  level {level}/{MASTERY_MAX} — pass mixed sets to level up
                </>
              )}
            </div>
          )}

          <div className="v2-lengths" role="radiogroup" aria-label="questions per run">
            <span className="v2-lengths-label">questions</span>
            {SESSION_LENGTHS.map((n) => (
              <button
                key={n}
                role="radio"
                aria-checked={n === length}
                className={`v2-len ${n === length ? "on" : ""}`}
                onClick={() => setLength(n)}
              >
                <span className="mono">{n}</span>
              </button>
            ))}
          </div>

          <div className="v2-menu-sec">the scale</div>

          {/* the hands-on refresher — how every new key starts */}
          {keyName !== MIX_KEY && onWalk && (
            <button className="continue-card v2-kind-card v2-walk-card" onClick={onWalk}>
              <div className="ic">
                <Icon name="piano" />
              </div>
              <div className="txt">
                <div className="kicker">warm up</div>
                <div className="ttl">walk the scale</div>
                <div className="sub">build it on the piano, step by step</div>
              </div>
              <span className="chev">›</span>
            </button>
          )}

          {scaleKinds.map(kindCard)}

          {chordKinds.length > 0 && (
            <>
              <div className="v2-menu-sec">the chords it grows</div>

              {keyName !== MIX_KEY && onChordWalk && (
                <button
                  className="continue-card v2-kind-card v2-walk-card"
                  onClick={onChordWalk}
                >
                  <div className="ic">
                    <Icon name="piano" />
                  </div>
                  <div className="txt">
                    <div className="kicker">warm up</div>
                    <div className="ttl">stack the chords</div>
                    <div className="sub">
                      build all seven triads from the scale, 1-3-5
                    </div>
                  </div>
                  <span className="chev">›</span>
                </button>
              )}

              {chordKinds.map(kindCard)}
            </>
          )}

          {invKinds.length > 0 && (
            <>
              <div className="v2-menu-sec">the flips — inversions</div>

              {keyName !== MIX_KEY && onInvWalk && (
                <button
                  className="continue-card v2-kind-card v2-walk-card"
                  onClick={onInvWalk}
                >
                  <div className="ic">
                    <Icon name="piano" />
                  </div>
                  <div className="txt">
                    <div className="kicker">warm up</div>
                    <div className="ttl">flip the chords</div>
                    <div className="sub">
                      take I, IV and V through all three positions
                    </div>
                  </div>
                  <span className="chev">›</span>
                </button>
              )}

              {invKinds.map(kindCard)}
            </>
          )}

          <div className="footnote">
            <Keys />
            <br />
            practice runs have no hearts — finish every question, chase your best
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrillMenuScreen;
